import { useMemo, useState } from 'react';
import { Unplug } from 'lucide-react';
import {
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ClientTelemetryDay,
  TelemetryScoreClientResponse,
  TelemetryScoreHistoryClientItem,
} from '@siliconx/shared';
import {
  Card,
  CardLabel,
  DeltaChip,
  EmptyState,
  MiniSpark,
  MiniTable,
  NO_TELEMETRY_AGENT_MESSAGE,
  ConnectIntegrationRemedy,
  TableToggleFooter,
  baseline30d,
  downloadCsv,
} from '../shared';
import { VerifyAssetAction } from '../../verification/VerifyAssetAction';
import { EvidenceSourcesPanel } from '../../verification/EvidenceSourcesPanel';

/**
 * SIL-565: Health & condition tab — health score trend, subsystem scores, and
 * device telemetry. Split out of AssetDetailV2.tsx; content unchanged from
 * the prior inline render (SIL-336/SIL-338 origin).
 */

// ---------------------------------------------------------------------------
// Health score trend on horizon bands
// ---------------------------------------------------------------------------

const BAND_ZONES = [
  { from: 85, to: 100, fill: '#059669', label: 'Healthy' },
  { from: 70, to: 85, fill: '#f59e0b', label: 'Degraded' },
  { from: 50, to: 70, fill: '#ea580c', label: 'Warning' },
  { from: 0, to: 50, fill: '#ef4444', label: 'Critical' },
];

function HealthTrendCard({ history }: { history: TelemetryScoreHistoryClientItem[] }) {
  const [open, setOpen] = useState(false);
  // API returns newest-first; charts read oldest→newest.
  const points = useMemo(
    () =>
      [...history].reverse().map((r) => ({
        t: r.scoredAt,
        label: new Date(r.scoredAt).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
        score: r.overallScore,
        coverage: r.overallCoverage,
        gap: r.overallCoverage < 1,
      })),
    [history],
  );
  const gapCount = points.filter((p) => p.gap).length;
  const yMin = Math.min(50, ...points.map((p) => Math.floor(p.score / 10) * 10));

  return (
    <Card>
      <CardLabel>Health score trend</CardLabel>
      {points.length === 0 ? (
        <EmptyState
          icon={Unplug}
          message={NO_TELEMETRY_AGENT_MESSAGE}
          remedy={<ConnectIntegrationRemedy />}
        />
      ) : (
        <>
          <div className="mt-3.5">
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -22 }}>
                {BAND_ZONES.filter((z) => z.to > yMin).map((z) => (
                  <ReferenceArea
                    key={z.label}
                    y1={Math.max(z.from, yMin)}
                    y2={z.to}
                    fill={z.fill}
                    fillOpacity={0.07}
                    stroke="none"
                  />
                ))}
                <XAxis dataKey="label" fontSize={12} tickLine={false} stroke="#a1a1aa" minTickGap={40} />
                <YAxis domain={[yMin, 100]} ticks={[50, 70, 85, 100].filter((t) => t >= yMin)} fontSize={12} tickLine={false} stroke="#a1a1aa" />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 6,
                    borderColor: 'hsl(var(--n-200))',
                    backgroundColor: 'hsl(var(--popover))',
                    color: 'hsl(var(--popover-foreground))',
                  }}
                  formatter={(v: unknown, name: unknown) =>
                    name === 'coverage'
                      ? `${Math.round((v as number) * 100)}%`
                      : Math.round(v as number)
                  }
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#047857"
                  strokeWidth={2}
                  dot={(props: { cx?: number; cy?: number; payload?: { gap?: boolean }; index?: number }) =>
                    props.payload?.gap ? (
                      <circle key={props.index} cx={props.cx} cy={props.cy} r={3.5} fill="#f59e0b" />
                    ) : (
                      <circle key={props.index} cx={props.cx} cy={props.cy} r={0} fill="none" />
                    )
                  }
                  name="Health score"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-xs text-zinc-500">
            A sustained drop below <span className="font-mono">85</span> leaves the healthy band
            and re-grades the asset.
            {gapCount > 0 && (
              <>
                {' '}
                <span className="text-amber-700">●</span> marks telemetry coverage gaps — score
                dips there reflect missing data, not condition.
              </>
            )}
          </div>
          <TableToggleFooter
            open={open}
            onToggle={() => setOpen(!open)}
            onExport={() =>
              downloadCsv(
                'health-score-trend.csv',
                ['Time', 'Health score', 'Coverage'],
                points.map((p) => [p.t, p.score, `${Math.round(p.coverage * 100)}%`]),
              )
            }
          />
          {open && (
            <MiniTable
              header={['Time', 'Health score', 'Coverage']}
              rows={points.map((p) => [p.label, Math.round(p.score), `${Math.round(p.coverage * 100)}%`])}
              numericCols={[1, 2]}
            />
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Subsystem scores
// ---------------------------------------------------------------------------

/**
 * Scoring engine categories are always `<Name>_Health` (e.g. `GPU_Health`,
 * `Validation_Health` — see telemetry-daily-agg-mapping.test.ts). The suffix
 * is redundant on every row of a card already titled "Subsystem scores", so
 * it's dropped; acronyms (GPU, CPU) are kept upper-case rather than title-cased.
 */
function humanizeSubsystem(category: string): string {
  return category
    .replace(/_health$/i, '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (/^[A-Z0-9]{2,4}$/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
}

function bandColor(score: number): string {
  if (score >= 85) return '#059669';
  if (score >= 70) return '#f59e0b';
  if (score >= 50) return '#ea580c';
  return '#ef4444';
}

function SubsystemCard({
  score,
  history,
}: {
  score: TelemetryScoreClientResponse | null;
  history: TelemetryScoreHistoryClientItem[];
}) {
  const [open, setOpen] = useState(false);
  const cats = score?.categories ?? [];
  const base = baseline30d(history, (r) => r.scoredAt);
  const deltaFor = (category: string): number | null => {
    const prev = base?.categories.find((c) => c.category === category);
    return prev ? (cats.find((c) => c.category === category)?.score ?? 0) - prev.score : null;
  };
  // Zoomed scale so uniformly-high fleets stay legible (locked design decision).
  const floor = cats.length
    ? Math.min(90, Math.max(0, Math.floor(Math.min(...cats.map((c) => c.score)) / 10) * 10))
    : 0;

  const rows = cats.map((c) => ({
    name: c.category,
    label: humanizeSubsystem(c.category),
    score: c.score,
    coverage: c.coverage,
    delta: deltaFor(c.category),
  }));

  return (
    <Card>
      <div className="mb-4 flex items-baseline justify-between">
        <CardLabel>Subsystem scores</CardLabel>
        {rows.length > 0 && (
          <span className="font-mono text-[13px] text-zinc-500">score · coverage · &Delta; 30d</span>
        )}
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={Unplug}
          message={NO_TELEMETRY_AGENT_MESSAGE}
          remedy={<ConnectIntegrationRemedy />}
        />
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            {rows.map((r) => {
              // SIL-333: zero-coverage subsystems must never render as a
              // scored-zero — a solid 0-width bar + numeral "0" was visually
              // identical to "genuinely bottomed out." DESIGN.md reserves the
              // dashed stroke for exactly "no data"; use it here too, and say
              // so in words rather than a bare digit.
              const noData = r.coverage <= 0;
              return (
                <div key={r.name} className="flex items-center gap-2.5">
                  <span
                    className="w-[92px] truncate text-xs text-zinc-500"
                    title={r.label}
                  >
                    {r.label}
                  </span>
                  <div
                    className={`relative h-2 flex-1 overflow-hidden rounded-full ${
                      noData ? 'border border-dashed border-zinc-300 bg-transparent' : 'bg-zinc-100'
                    }`}
                  >
                    {!noData && (
                      <div
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          width: `${((r.score - floor) / (100 - floor)) * 100}%`,
                          background: bandColor(r.score),
                        }}
                      />
                    )}
                  </div>
                  <span
                    className={`w-14 text-right font-mono text-[13px] ${
                      noData ? 'font-normal text-zinc-400' : 'font-semibold text-zinc-900'
                    }`}
                  >
                    {noData ? 'No data' : Math.round(r.score)}
                  </span>
                  <span className="w-9 text-right font-mono text-[13px] text-zinc-400">
                    {Math.round(r.coverage * 100)}%
                  </span>
                  <span
                    className={`w-9 text-right font-mono text-[13px] ${
                      r.delta == null || Math.abs(r.delta) < 0.5
                        ? 'text-zinc-400'
                        : r.delta > 0
                          ? 'text-emerald-700'
                          : 'text-amber-700'
                    }`}
                  >
                    {r.delta == null ? '' : `${r.delta > 0 ? '+' : ''}${r.delta.toFixed(0)}`}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3.5 text-xs text-zinc-500">
            Bars span <span className="font-mono">{floor}–100</span>
          </div>
          <TableToggleFooter
            open={open}
            onToggle={() => setOpen(!open)}
            onExport={() =>
              downloadCsv(
                'subsystem-scores.csv',
                ['Subsystem', 'Score', 'Coverage', 'Delta 30d'],
                rows.map((r) => [
                  r.label,
                  r.score,
                  `${Math.round(r.coverage * 100)}%`,
                  r.delta?.toFixed(1) ?? '',
                ]),
              )
            }
          />
          {open && (
            <MiniTable
              header={['Subsystem', 'Score', 'Coverage', 'Δ 30d']}
              numericCols={[1, 2, 3]}
              rows={rows.map((r) => [
                r.label,
                Math.round(r.score),
                `${Math.round(r.coverage * 100)}%`,
                r.delta == null ? '—' : r.delta.toFixed(1),
              ])}
            />
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Device telemetry — sparkline tiles + toggle table
// ---------------------------------------------------------------------------

function TelemetryCard({ rows }: { rows: ClientTelemetryDay[] }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Oldest→newest for sparklines; API order is oldest-first already for the
  // v1 sparkline, but sort defensively by day.
  const days = useMemo(() => [...rows].sort((a, b) => a.day.localeCompare(b.day)), [rows]);
  const series = (pick: (d: ClientTelemetryDay) => number | null) =>
    days.map(pick).filter((v): v is number => v != null);

  const temp = series((d) => d.avgGpuTempC);
  const util = series((d) => d.avgGpuUtilPct);
  const power = series((d) => d.avgPowerDrawWatts);

  const last = (vals: number[]): number | null => vals[vals.length - 1] ?? null;
  const delta = (vals: number[]) => (vals.length >= 2 ? vals[vals.length - 1]! - vals[0]! : null);
  const dTemp = delta(temp);
  const dUtil = delta(util);
  const dPower = delta(power);

  const visible = showAll ? [...days].reverse() : [...days].reverse().slice(0, 9);
  const fmt = (v: number | null, digits = 1) => (v == null ? '—' : v.toFixed(digits));

  return (
    <Card>
      <CardLabel>Device telemetry · last 30 days</CardLabel>
      {days.length === 0 ? (
        <EmptyState
          icon={Unplug}
          message={NO_TELEMETRY_AGENT_MESSAGE}
          remedy={<ConnectIntegrationRemedy />}
        />
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-8 sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">GPU temp (avg)</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-semibold text-zinc-900">
                  {last(temp) != null ? `${last(temp)!.toFixed(1)}°C` : '—'}
                </span>
                {dTemp != null && (
                  <DeltaChip
                    text={`${dTemp >= 0 ? '+' : ''}${dTemp.toFixed(1)}° /30d`}
                    warn={dTemp >= 1}
                  />
                )}
              </div>
              <MiniSpark values={temp} stroke="#047857" fill />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">GPU utilization</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-semibold text-zinc-900">
                  {last(util) != null ? `${last(util)!.toFixed(1)}%` : '—'}
                </span>
                {dUtil != null && (
                  <DeltaChip text={`${dUtil >= 0 ? '+' : ''}${dUtil.toFixed(1)} pts`} warn={false} />
                )}
              </div>
              <MiniSpark values={util} stroke="#6366f1" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Power draw</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-semibold text-zinc-900">
                  {last(power) != null ? `${Math.round(last(power)!)} W` : '—'}
                </span>
                {dPower != null && (
                  <DeltaChip
                    text={`${dPower >= 0 ? '+' : ''}${Math.round(dPower)} W /30d`}
                    warn={dPower >= 5}
                  />
                )}
              </div>
              <MiniSpark values={power} stroke="#d97706" fill />
            </div>
          </div>
          <TableToggleFooter
            open={open}
            onToggle={() => setOpen(!open)}
            onExport={() =>
              downloadCsv(
                'device-telemetry.csv',
                ['Date', 'C avg', 'C max', 'GPU %', 'W'],
                [...days]
                  .reverse()
                  .map((d) => [d.day, d.avgGpuTempC, d.maxGpuTempC, d.avgGpuUtilPct, d.avgPowerDrawWatts]),
              )
            }
          />
          {open && (
            <>
              <MiniTable
                header={['Date', '°C avg', '°C max', 'GPU %', 'W']}
                numericCols={[1, 2, 3, 4]}
                rows={visible.map((d) => [
                  d.day,
                  fmt(d.avgGpuTempC),
                  fmt(d.avgGpuTempC == null ? null : d.maxGpuTempC),
                  fmt(d.avgGpuUtilPct),
                  d.avgPowerDrawWatts == null ? '—' : String(Math.round(d.avgPowerDrawWatts)),
                ])}
              />
              {!showAll && days.length > 9 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="mt-1.5 text-xs text-zinc-500 hover:text-zinc-700"
                >
                  Showing 9 of {days.length} · view all
                </button>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab assembly
// ---------------------------------------------------------------------------

export function HealthTab({
  assetId,
  score,
  scoreHistory,
  telemetryRecent,
}: {
  assetId: string;
  score: TelemetryScoreClientResponse | null;
  scoreHistory: TelemetryScoreHistoryClientItem[];
  telemetryRecent: ClientTelemetryDay[];
}) {
  return (
    <>
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[2fr_1fr]">
        <HealthTrendCard history={scoreHistory} />
        <SubsystemCard score={score} history={scoreHistory} />
      </div>
      <TelemetryCard rows={telemetryRecent} />
      <EvidenceSourcesPanel assetId={assetId} />
      <VerifyAssetAction assetId={assetId} />
    </>
  );
}
